/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Renders cars grouped by category, and within each category one card per unique make+model
 * (deduped, with year range + count + price), mirroring the aegis-ao-rental Home layout.
 */

import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Car } from '../types';
import { priceUnit, fmtPrice } from '../utils/pricing';
import { categoryLabel } from '../utils/localized';
import ModelImage from './ModelImage';

interface ModelGroup {
  key: string;
  label: string; // "MAKE MODEL" (or the car title when make/model are empty)
  make?: string;
  model?: string;
  years: number[];
  count: number;
  maxPrice: number;
  pricingPeriod?: number;
  image?: string;
  typeColor?: string;
  sampleCarId: string;
}

function buildGroups(cars: Car[], lang: string): { category: string; models: ModelGroup[] }[] {
  const byCategory = new Map<string, Map<string, ModelGroup>>();
  const categoryOrder = new Map<string, number>();

  for (const car of cars) {
    // Group by vehicle segment (CarCategories), localized to the UI language (NameJson → base name).
    const category = categoryLabel(car, lang, 'Other');
    categoryOrder.set(
      category,
      Math.min(categoryOrder.get(category) ?? 99, car.categoryPosition ?? 99),
    );
    const make = (car.brand || '').trim();
    const model = (car.model || '').trim();
    const label = `${make} ${model}`.trim() || car.title || 'Vehicle';
    const key = label.toUpperCase();
    const price = car.priceFullTimePerWeek || car.pricePerWeek || 0;

    if (!byCategory.has(category)) byCategory.set(category, new Map());
    const models = byCategory.get(category)!;

    const existing = models.get(key);
    if (!existing) {
      models.set(key, {
        key,
        label,
        make,
        model,
        years: car.year ? [car.year] : [],
        count: 1,
        maxPrice: price,
        pricingPeriod: car.pricingPeriod,
        image: car.imageURL,
        typeColor: car.typeColor,
        sampleCarId: car.id,
      });
    } else {
      existing.count += 1;
      if (car.year && !existing.years.includes(car.year)) existing.years.push(car.year);
      if (price > existing.maxPrice) existing.maxPrice = price;
      if (!existing.image && car.imageURL) existing.image = car.imageURL;
    }
  }

  return Array.from(byCategory.entries())
    .sort(
      (a, b) =>
        (categoryOrder.get(a[0]) ?? 99) - (categoryOrder.get(b[0]) ?? 99) ||
        a[0].localeCompare(b[0]),
    )
    .map(([category, models]) => ({
      category,
      models: Array.from(models.values()),
    }));
}

function yearRange(years: number[]): string {
  if (years.length === 0) return '';
  const min = Math.min(...years);
  const max = Math.max(...years);
  return min === max ? `${min}` : `${min}–${max}`;
}

/** Link target for a model card: the model-booking page (resolves the first available car),
 *  carrying the selected city so the main-domain (cross-tenant) resolve is scoped. Falls back to
 *  the specific car when make/model are missing. */
function modelHref(m: ModelGroup, city?: string): string {
  if (!m.make && !m.model) return `/car/${m.sampleCarId}`;
  const q = city ? `?city=${encodeURIComponent(city)}` : '';
  return `/model/${encodeURIComponent(m.make || '')}/${encodeURIComponent(m.model || '')}${q}`;
}

export default function ModelCategoryList({ cars, city }: { cars: Car[]; city?: string }) {
  const { t, i18n } = useTranslation();
  const groups = buildGroups(cars, i18n.language);

  return (
    <div className="space-y-12">
      {groups.map(({ category, models }) => (
        <div key={category} className="space-y-5">
          <h3 className="text-2xl font-bold text-gray-900">
            {category} <span className="text-gray-400 font-normal text-lg">· {models.length}</span>
          </h3>

          <div className="overflow-x-auto pb-3 -mx-1 px-1 model-cards-scroll">
            <div className="flex gap-5" style={{ minWidth: 'max-content' }}>
              {models.map((m) => {
                const amount = m.maxPrice;
                const unit = t(`units.${priceUnit(m.pricingPeriod)}`);
                const years = yearRange(m.years);
                return (
                  <Link
                    key={m.key}
                    to={modelHref(m, city)}
                    className="group flex-shrink-0 block text-gray-900 bg-white rounded-2xl overflow-hidden ring-1 ring-gray-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    style={{ width: 240, minWidth: 240 }}
                  >
                    <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                      <ModelImage
                        make={m.make}
                        model={m.model}
                        fallback={m.image}
                        alt={m.label}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {m.maxPrice > 0 && (
                        <div className="absolute top-2.5 right-2.5 bg-yellow-500 text-black px-2.5 py-0.5 rounded-full text-xs font-semibold shadow-sm">
                          ${fmtPrice(amount)}/{unit}
                        </div>
                      )}
                      <div className="absolute bottom-2.5 left-2.5 bg-green-600 text-white px-2.5 py-0.5 rounded-full text-[11px] font-semibold flex items-center gap-1 shadow-sm">
                        <Users className="h-3 w-3" /> {t('card.available', { n: m.count })}
                      </div>
                    </div>
                    <div className="p-3">
                      <h4 className="font-semibold text-[15px] leading-tight truncate text-gray-900">{m.label}</h4>
                      {years && <p className="mt-0.5 text-xs text-gray-500">{t('card.years', { range: years })}</p>}
                      <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex justify-between items-center gap-2">
                        <span className="font-bold text-sm text-gray-900">
                          ${fmtPrice(amount)}
                          <span className="text-[11px] font-normal text-gray-500"> / {unit}</span>
                        </span>
                        <span className="btn-primary text-xs py-1.5 px-3.5 shrink-0">
                          {t('card.book')}
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
